import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLinkIcon, FileTextIcon } from 'lucide-react';
import { site, parcours } from '../data/site';
import { QualiopiBadge } from '../components/QualiopiBadge';
import { FaqList } from '../components/FaqList';
import { useSeo } from '../hooks/useSeo';
const engagements = [{
  titre: 'Une information claire avant l’inscription',
  texte: 'Objectifs, public concerné, programme, durée, tarif, prérequis, format, délai d’accès et modalités d’évaluation sont publiés pour chaque formation.'
}, {
  titre: 'Un besoin analysé au préalable',
  texte: 'Un échange permet d’identifier les objectifs, le profil du bénéficiaire et les éventuels besoins d’adaptation avant toute inscription.'
}, {
  titre: 'Des évaluations formalisées',
  texte: 'Selon le parcours : positionnement, quiz, exercices, cas pratiques, mises en situation et évaluation finale.'
}, {
  titre: 'Une amélioration continue',
  texte: 'Les retours des bénéficiaires sont analysés afin d’ajuster les prestations lorsque cela est nécessaire.'
}];
export function Qualiopi() {
  useSeo({
    title: 'Qualité & certification Qualiopi — Zillenium Academy',
    description: 'Zillenium Academy est un organisme de formation certifié Qualiopi. Découvrez nos engagements qualité, les modalités générales et les documents consultables.'
  });
  return <>
      <section className="bg-brand-900" data-id="element-496" data-mp="3bbn92le7p0y5">
        <div className="mx-auto max-w-content px-5 py-14 lg:px-8 lg:py-20" data-id="element-497" data-mp="3g959fne5y7uj">
          <QualiopiBadge tone="dark" data-id="element-498" data-mp="3g72kz7snsmrx" />
          <h1 className="mt-6 max-w-3xl text-4xl font-extrabold leading-tight text-white md:text-[46px]" data-id="element-499" data-mp="3l7q6xf216dfa">
            Qualité & certification Qualiopi
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-brand-200" data-id="element-500" data-mp="3q0rra2l0b569">
            La certification Qualiopi atteste de la qualité du processus mis en œuvre pour nos
            actions de formation. Elle engage notre organisation sur la clarté de l’information, la
            conception des parcours et le suivi des bénéficiaires.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-content px-5 py-16 lg:px-8" data-id="element-501" data-mp="2lg4eki6lk52g">
        <h2 className="text-3xl font-bold text-ink" data-id="element-502" data-mp="2z6g5idrg3xs0">Nos engagements</h2>
        <div className="mt-8 grid gap-x-12 gap-y-8 md:grid-cols-2" data-id="element-503" data-mp="2u86aq49muz6x">
          {engagements.map(e => <article key={e.titre} className="border-t border-surface-line pt-5" data-id="element-504" data-mp="26iby9nt87cn7">
              <h3 className="text-lg font-bold text-ink" data-id="element-505" data-mp="22hs2oooawh36">{e.titre}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-muted" data-id="element-506" data-mp="22jlcudy4hjfx">{e.texte}</p>
            </article>)}
        </div>
      </section>

      <section className="bg-surface-soft" data-id="element-507" data-mp="2bh5cxjk919oy">
        <div className="mx-auto max-w-content px-5 py-16 lg:px-8" data-id="element-508" data-mp="2bk9pp5bs5dr8">
          <h2 className="text-3xl font-bold text-ink" data-id="element-509" data-mp="3rv58yjfz4f6x">Modalités générales</h2>
          <ol className="mt-8 grid gap-x-8 gap-y-8 md:grid-cols-2" data-id="element-510" data-mp="3my5246bav8er">
            {parcours.map((p, i) => <li key={p.titre} className="flex gap-4" data-id="element-511" data-mp="18ojb603zbxjq" data-mpi={i}>
                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-sm font-bold text-brand-800 ring-1 ring-surface-line" data-id="element-512" data-mp="1donudxw9v6dn">
                  {i + 1}
                </span>
                <div data-id="element-513" data-mp="14nhcplhvzpit">
                  <h3 className="font-bold text-ink" data-id="element-514" data-mp="14p0vbir0mcnr">{p.titre}</h3>
                  <p className="mt-1 text-[15px] leading-relaxed text-ink-muted" data-id="element-515" data-mp="1sdtudo2xp0ml">{p.texte}</p>
                </div>
              </li>)}
          </ol>

          <div className="mt-10 grid gap-4 md:grid-cols-2" data-id="element-516" data-mp="1wedrwsrf6699">
            <article className="rounded-xl border border-surface-line bg-white p-6" data-id="element-517" data-mp="1impdujxm17pn">
              <h3 className="font-bold text-ink" data-id="element-518" data-mp="1nmbtt14tp225">Documents remis</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-muted" data-id="element-519" data-mp="1nkil7gang7ri">
                Attestation de fin de formation ou attestation de réalisation, émargements et
                questionnaire de satisfaction.
              </p>
            </article>
            <article className="rounded-xl border border-surface-line bg-white p-6" data-id="element-520" data-mp="ri4lp45ec1o9">
              <h3 className="font-bold text-ink" data-id="element-521" data-mp="mh73wsoat9na">Accessibilité</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-muted" data-id="element-522" data-mp="igdgs1iojyvg">
                Zillenium Academy étudie les aménagements possibles en fonction des besoins du
                bénéficiaire.{' '}
                <Link to="/accessibilite" className="font-semibold text-accent-600 hover:underline" data-id="element-523" data-mp="1a8clbc9w09zp">
                  En savoir plus
                </Link>
                .
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-content px-5 py-16 lg:px-8" data-id="element-524" data-mp="11deohu3mcd33">
        <h2 className="text-3xl font-bold text-ink" data-id="element-525" data-mp="wfmxd2assuzy">Documents consultables</h2>
        <p className="mt-3 max-w-2xl text-[17px] text-ink-muted" data-id="element-526" data-mp="1tw25axbl15yd">
          Certificat Qualiopi, catalogue de formations et conditions générales de vente sont
          accessibles depuis nos liens publics.
        </p>
        <ul className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-527" data-mp="1whjnjyr9pmka">
          {['Certificat Qualiopi', 'Catalogue de formations', 'Conditions Générales de Vente'].map(doc => <li key={doc} data-id="element-528" data-mp="26e65aeoarxi3">
                <a href={site.linktree} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-4 rounded-xl border border-surface-line bg-white px-5 py-4 transition-colors duration-150 ease-out hover:border-accent-400" data-id="element-529" data-mp="26cuuhuwfpphh">
                  <span className="inline-flex items-center gap-3 text-[15px] font-semibold text-ink" data-id="element-530" data-mp="2p4xbj7czn7s9">
                    <FileTextIcon size={18} className="text-accent-600" aria-hidden data-id="element-531" data-mp="2u3qrr7jntcw4" />
                    {doc}
                  </span>
                  <ExternalLinkIcon size={16} className="text-ink-faint" aria-hidden data-id="element-532" data-mp="2y6n2yd6ve6q1" />
                </a>
              </li>)}
        </ul>
      </section>

      <section className="bg-surface-soft" data-id="element-533" data-mp="131xjooxtk9fx">
        <div className="mx-auto max-w-content px-5 py-16 lg:px-8" data-id="element-534" data-mp="132p2ez6bicos">
          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]" data-id="element-535" data-mp="z1n3kjgbc2nc">
            <div data-id="element-536" data-mp="1cs8p6fx5iqox">
              <h2 className="text-3xl font-bold text-ink" data-id="element-537" data-mp="1css21i7y2ow3">Questions fréquentes</h2>
              <p className="mt-3 text-[17px] text-ink-muted" data-id="element-538" data-mp="181s9sq9rsuzx">
                Une question sur nos modalités ?{' '}
                <Link to="/contact" className="font-semibold text-accent-600 hover:underline" data-id="element-539" data-mp="1mpn2i6ns2zd0">
                  Écrivez-nous
                </Link>{' '}
                ou appelez le {site.telephone}.
              </p>
            </div>
            <FaqList data-id="element-540" data-mp="1vqkyc1o2c0ki" />
          </div>
        </div>
      </section>
    </>;
}